export type ListeningDays = Record<string, Record<string, number>>;
type Sample = { trackId: string; position: number; wall: number; monotonic: number; rate: number; active: boolean };
export const listeningDay = (time: number) => {
  const date = new Date(time);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

// Only observed media progress earns listening time. A seek, stalled stream,
// suspended tab, or changed track resets the baseline without crediting a gap.
export class ListeningLedger {
  days: ListeningDays;
  private previous: Sample | null = null;
  constructor(saved: unknown = {}) {
    this.days = {};
    if (saved && typeof saved === 'object') {
      for (const [day, tracks] of Object.entries(saved)) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !tracks || typeof tracks !== 'object') continue;
        this.days[day] = Object.fromEntries(Object.entries(tracks).filter((entry): entry is [string, number] => {
          const [id, seconds] = entry;
          return id.length > 0 && id.length < 500 && typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0 && seconds <= 86400;
        }));
      }
    }
    this.prune();
  }
  reset() { this.previous = null; }
  sample(sample: Sample) {
    const before = this.previous;
    this.previous = sample;
    if (!before?.active || before.trackId !== sample.trackId || !sample.trackId) return false;
    const elapsed = (sample.monotonic - before.monotonic) / 1000;
    const progress = sample.position - before.position;
    if (elapsed <= 0 || elapsed > 15 || progress <= 0 || !Number.isFinite(progress) ||
        !Number.isFinite(before.rate) || before.rate <= 0 || Math.abs(progress - elapsed * before.rate) > Math.max(0.75, elapsed * 0.35)) return false;
    const end = sample.wall;
    let start = end - elapsed * 1000;
    while (start < end) {
      const date = new Date(start);
      const boundary = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1).getTime();
      const until = Math.min(end, boundary);
      const key = listeningDay(start);
      const tracks = this.days[key] ||= {};
      tracks[sample.trackId] = Math.min(86400, (tracks[sample.trackId] || 0) + (until - start) / 1000);
      start = until;
    }
    this.prune();
    return true;
  }
  summary(wall = Date.now()) {
    const tracks = this.days[listeningDay(wall)] || {};
    return { seconds: Object.values(tracks).reduce((sum, seconds) => sum + seconds, 0), songs: Object.values(tracks).filter(seconds => seconds >= 5).length };
  }
  private prune() {
    const keys = Object.keys(this.days).sort().reverse();
    for (const key of keys.slice(90)) delete this.days[key];
  }
}
